import React, { useState, useEffect, useMemo, useCallback } from "react";

/* ════════════════════════════════════════════════════════════════════
   SÜPER LIG CHALLENGE — Football Quiz Hub
   • Career Path (4 difficulty categories)
   • Starting XI Challenge (derby lineups on pitch)
   • Tic-Tac-Toe Football (club crossword with difficulty tiers)
   • Dark premium aesthetic matching Galatasaray Hub
   ════════════════════════════════════════════════════════════════════ */

const shuffle = a => { const b=[...a]; for(let i=b.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[b[i],b[j]]=[b[j],b[i]];} return b; };

const normalize = (s) => (s || "")
  .replace(/\u0130/g,"I")  // İ → I (before toLowerCase to avoid i̇ issue)
  .replace(/\u0131/g,"i")  // ı → i
  .toLowerCase()
  .replace(/\u015f/g,"s").replace(/\u015e/g,"s")  // ş Ş
  .replace(/\u00e7/g,"c").replace(/\u00c7/g,"c")  // ç Ç
  .replace(/\u011f/g,"g").replace(/\u011e/g,"g")  // ğ Ğ
  .replace(/\u00f6/g,"o").replace(/\u00d6/g,"o")  // ö Ö
  .replace(/ü/g,"u").replace(/\u00dc/g,"u")  // ü Ü
  .replace(/\u00e2/g,"a").replace(/\u00ee/g,"i")
  .replace(/\u00e9/g,"e").replace(/\u00e8/g,"e").replace(/\u00eb/g,"e").replace(/\u00f1/g,"n")
  .replace(/\u00e1/g,"a").replace(/\u00e0/g,"a").replace(/\u00e3/g,"a").replace(/\u00fa/g,"u")
  .replace(/\u00f3/g,"o").replace(/\u00f4/g,"o").replace(/\u00ed/g,"i").replace(/\u00fd/g,"y")
  .replace(/\u017e/g,"z").replace(/\u010d/g,"c").replace(/\u0159/g,"r").replace(/\u00f0/g,"d")
  .replace(/\u0107/g,"c").replace(/\u0106/g,"c")  // ć Ć
  .replace(/\u0161/g,"s").replace(/\u0160/g,"s")  // š Š (non-Turkish)
  .replace(/\u0111/g,"d").replace(/\u0110/g,"d")  // đ Đ
  .replace(/\u0144/g,"n").replace(/\u015b/g,"s")  // ń ś
  .replace(/\u00fe/g,"th").replace(/\u00e6/g,"ae").replace(/\u00f8/g,"o").replace(/\u00e5/g,"a")
  .replace(/i\u0307/g,"i");  // combining dot above (from İ.toLowerCase())

// Club colors for the Big 4
const CLUB_COLORS = {
  "Galatasaray": { primary: "#FDB913", secondary: "#C8102E", bg: "#1a1008" },
  "Fenerbahçe": { primary: "#FFED00", secondary: "#00205B", bg: "#0a0a1a" },
  "Beşiktaş": { primary: "#FFFFFF", secondary: "#000000", bg: "#111111" },
  "Trabzonspor": { primary: "#7B2D26", secondary: "#1B4B8A", bg: "#0a0a1a" },
};
const BIG_CLUBS = new Set(["Galatasaray", "Fenerbahçe", "Beşiktaş", "Trabzonspor"]);

const getClubColor = (name) => {
  if (!name) return "#c8102e";
  const n = name.toLowerCase();
  if (n.includes("galatasaray")) return "#FDB913";
  if (n.includes("fenerbah")) return "#FFED00";
  if (n.includes("beşiktaş") || n.includes("besiktas")) return "#e0e0e0";
  if (n.includes("trabzon")) return "#7B2D26";
  return "#c8102e";
};

// ── Reusable Player Autocomplete ──────────────────
function PlayerAutocomplete({ players, onSelect, placeholder = "Type player name...", autoFocus = false, openUp = false }) {
  const [input, setInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);

  const matches = useMemo(() => {
    if (!input || input.length < 1) return [];
    const q = normalize(input);
    return players
      .filter(name => {
        const n = normalize(name);
        return n.includes(q) || name.split(" ").some(w => normalize(w).startsWith(q));
      })
      .slice(0, 12);
  }, [input, players]);

  const select = (name) => {
    onSelect(name);
    setInput("");
    setShowDropdown(false);
    setHighlightIdx(0);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (matches.length > 0) select(matches[highlightIdx] || matches[0]); else if (input.trim()) select(input.trim()); }
    else if (e.key === "Escape") setShowDropdown(false);
  };

  const dropdownStyle = openUp
    ? { position: "absolute", bottom: "100%", left: 0, right: 0, zIndex: 50, background: "#151515", border: "2px solid #333", borderBottom: "none", borderRadius: "8px 8px 0 0", maxHeight: 260, overflowY: "auto", boxShadow: "0 -8px 24px rgba(0,0,0,.6)" }
    : { position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, background: "#151515", border: "2px solid #333", borderTop: "none", borderRadius: "0 0 8px 8px", maxHeight: 260, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.6)" };

  return (
    <div style={{ position: "relative", flex: 1 }}>
      <input
        value={input}
        onChange={e => { setInput(e.target.value); setShowDropdown(true); setHighlightIdx(0); }}
        onFocus={() => setShowDropdown(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={{ width: "100%", background: "#0e0e0e", border: "2px solid #333", borderRadius: 8, color: "#ddd", padding: "14px 16px", fontSize: 15, outline: "none", fontFamily: "'Outfit', sans-serif", boxSizing: "border-box" }}
      />
      {showDropdown && matches.length > 0 && (
        <div style={dropdownStyle}>
          {(openUp ? [...matches].reverse() : matches).map((name, i) => {
            const realIdx = openUp ? matches.length - 1 - i : i;
            return (
            <button key={i} onClick={() => select(name)} onMouseEnter={() => setHighlightIdx(realIdx)}
              style={{ display: "block", width: "100%", textAlign: "left", background: realIdx === highlightIdx ? "#222" : "transparent", border: "none", borderBottom: openUp ? "none" : "1px solid #252525", borderTop: openUp ? "1px solid #252525" : "none", color: "#ddd", padding: "12px 16px", fontSize: 14, fontFamily: "'Outfit', sans-serif", cursor: "pointer" }}>
              {name}
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Pitch SVG Component ──────────────────────────────────
function PitchLineup({ players, teamName, color = "#e74c3c", isAway = false, hiddenIndices = null, onPlayerClick = null, guesses = null, formation = "" }) {
  if (!players || players.length === 0) return null;
  const starters = players.filter(p => !p.substitute);
  const subs = players.filter(p => p.substitute);

  let formationStr = formation || "";
  if (!formationStr) {
    const defCount = starters.filter(p => (p.position||"").toUpperCase() === "D").length;
    const midCount = starters.filter(p => (p.position||"").toUpperCase() === "M").length;
    const fwdCount = starters.filter(p => (p.position||"").toUpperCase() === "F").length;
    if (defCount || midCount || fwdCount) formationStr = `${defCount}-${midCount}-${fwdCount}`;
  }

  const ratingColor = (r) => {
    const v = parseFloat(r);
    if (!v || isNaN(v)) return "#555";
    if (v >= 8) return "#1abc9c";
    if (v >= 7) return "#2ecc71";
    if (v >= 6.5) return "#f39c12";
    if (v >= 6) return "#e67e22";
    return "#e74c3c";
  };

  const cleanNum = (n) => { if (!n) return ""; const s = String(n); return s.endsWith(".0") ? s.slice(0, -2) : s.replace(/\.0$/, ""); };

  const flatMap = {};
  starters.forEach((p, i) => { flatMap[p.name] = i; });

  return (
    <div style={{ flex: 1, minWidth: 240, maxWidth: 380 }}>
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, color, textTransform: "uppercase", letterSpacing: 2 }}>{teamName}</span>
        {formationStr && <span style={{ fontSize: 11, color: "#666", marginLeft: 8, fontFamily: "'DM Mono', monospace" }}>{formationStr}</span>}
      </div>
      <div style={{
        position: "relative", width: "100%", paddingBottom: "130%",
        background: "linear-gradient(180deg, #174928 0%, #1b5a30 12%, #174928 25%, #1b5a30 37%, #174928 50%, #1b5a30 62%, #174928 75%, #1b5a30 87%, #174928 100%)",
        borderRadius: 8, overflow: "hidden", border: "1.5px solid #1e6b35",
      }}>
        {/* Pitch markings */}
        <svg viewBox="0 0 100 140" preserveAspectRatio="none" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}>
          <rect x="5" y="3" width="90" height="134" fill="none" stroke="#1e6b35" strokeWidth=".4" />
          <line x1="5" y1="70" x2="95" y2="70" stroke="#1e6b35" strokeWidth=".3" />
          <circle cx="50" cy="70" r="12" fill="none" stroke="#1e6b35" strokeWidth=".3" />
          <circle cx="50" cy="70" r=".6" fill="#1e6b35" />
          <rect x="25" y="3" width="50" height="18" fill="none" stroke="#1e6b35" strokeWidth=".3" />
          <rect x="25" y="119" width="50" height="18" fill="none" stroke="#1e6b35" strokeWidth=".3" />
          <rect x="35" y="3" width="30" height="7" fill="none" stroke="#1e6b35" strokeWidth=".3" />
          <rect x="35" y="130" width="30" height="7" fill="none" stroke="#1e6b35" strokeWidth=".3" />
          <path d="M 37 21 Q 50 28 63 21" fill="none" stroke="#1e6b35" strokeWidth=".3" />
          <path d="M 37 119 Q 50 112 63 119" fill="none" stroke="#1e6b35" strokeWidth=".3" />
        </svg>

        {(() => {
          const formationNums = formationStr ? formationStr.split("-").map(Number).filter(n => n > 0) : [];
          let allRows;
          if (formationNums.length > 0 && formationNums.reduce((a, b) => a + b, 0) === starters.length - 1) {
            const gk = starters.filter(p => (p.position || "").toUpperCase() === "G");
            const outfield = starters.filter(p => (p.position || "").toUpperCase() !== "G");
            allRows = [gk];
            let idx = 0;
            for (const count of formationNums) { allRows.push(outfield.slice(idx, idx + count)); idx += count; }
          } else {
            const gk = starters.filter(p => (p.position||"").toUpperCase() === "G");
            const defs = starters.filter(p => (p.position||"").toUpperCase() === "D");
            const mids = starters.filter(p => (p.position||"").toUpperCase() === "M");
            const fwds = starters.filter(p => (p.position||"").toUpperCase() === "F");
            allRows = [gk, defs, mids, fwds].filter(r => r.length > 0);
          }
          const displayRows = isAway ? [...allRows].reverse() : allRows;
          // For away teams, reverse each row's player order to mirror the pitch
          const finalRows = isAway ? displayRows.map(row => [...row].reverse()) : displayRows;
          const numRows = finalRows.length;
          const rowYs = finalRows.map((_, i) => { const margin = 8; return margin + (i / (numRows - 1 || 1)) * (100 - 2 * margin); });

          return finalRows.map((row, ri) => (
            <div key={ri} style={{ position: "absolute", top: `${rowYs[ri]}%`, left: 0, right: 0, display: "flex", justifyContent: "space-evenly", alignItems: "center", transform: "translateY(-50%)", padding: "0 6px" }}>
              {row.map((p, pi) => {
                const idx = flatMap[p.name];
                const isHidden = hiddenIndices && hiddenIndices.includes(idx);
                const guess = guesses && guesses[idx];
                const showName = !hiddenIndices || guess || !isHidden;
                const rating = parseFloat(p.rating);
                const hasGoal = parseInt(p.goals) > 0;

                return (
                  <div key={pi} style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: isHidden && !guess ? "pointer" : "default", zIndex: 10, minWidth: 38 }}
                    onClick={() => isHidden && !guess && onPlayerClick && onPlayerClick(idx)}>
                    {rating > 0 && showName && !hiddenIndices && (
                      <div style={{ fontSize: 7, fontWeight: 700, color: "#fff", background: ratingColor(p.rating), borderRadius: 3, padding: "1px 3px", marginBottom: 1, fontFamily: "'DM Mono', monospace", boxShadow: "0 1px 3px rgba(0,0,0,.5)" }}>{rating.toFixed(1)}</div>
                    )}
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%",
                      background: isHidden && !guess ? "linear-gradient(135deg, #3b82f6, #1d4ed8)" : guess?.correct ? "linear-gradient(135deg, #22c55e, #16a34a)" : guess && !guess.correct ? "linear-gradient(135deg, #ef4444, #dc2626)" : `linear-gradient(135deg, ${color}dd, ${color}88)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: `2px solid ${isHidden && !guess ? "#60a5fa" : "rgba(255,255,255,.3)"}`,
                      boxShadow: "0 2px 6px rgba(0,0,0,.5)", fontSize: 11, fontWeight: 800, color: "white",
                      fontFamily: "'Bebas Neue', sans-serif", letterSpacing: .5, position: "relative",
                    }}>
                      {isHidden && !guess ? "?" : cleanNum(p.number)}
                      {hasGoal && showName && <span style={{ position: "absolute", top: -5, right: -5, fontSize: 8 }}>⚽</span>}
                    </div>
                    <div style={{ fontSize: 11, color: showName ? "rgba(255,255,255,.95)" : "#60a5fa", fontFamily: "'Outfit', sans-serif", fontWeight: 500, textAlign: "center", marginTop: 2, textShadow: "0 1px 4px rgba(0,0,0,.9)", maxWidth: 70, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {showName ? (p.shortName || p.name?.split(" ").pop() || p.name) : "???"}
                    </div>
                    {guess && !guess.correct && (
                      <div style={{ fontSize: 6, color: "#ef4444", fontFamily: "monospace", textShadow: "0 1px 2px #000" }}>({guess.guess})</div>
                    )}
                  </div>
                );
              })}
            </div>
          ));
        })()}
      </div>

      {subs.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>
          {subs.map((p, i) => (
            <span key={i} style={{ fontSize: 10, color: "#777", background: "#151515", borderRadius: 4, padding: "3px 7px", fontFamily: "'DM Mono', monospace", border: "2px solid #222" }}>
              <span style={{ color, marginRight: 3, fontWeight: 600 }}>{cleanNum(p.number)}</span>
              {p.shortName || p.name?.split(" ").pop()}
              {p.rating && <span style={{ color: ratingColor(p.rating), marginLeft: 4, fontWeight: 600 }}>{parseFloat(p.rating).toFixed(1)}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════
export default function App() {
  const [page, setPage] = useState("home");
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch("/data/superlig_frontend_data.json")
      .then(r => r.json())
      .then(setData)
      .catch(console.error);
  }, []);

  if (!data) return (
    <div style={{ minHeight: "100vh", background: "#121212", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, letterSpacing: 6, background: "linear-gradient(135deg, #C8102E, #FDB913)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>SL</div>
        <div style={{ color: "#555", fontSize: 12, fontFamily: "'DM Mono', monospace", marginTop: 8 }}>Loading...</div>
      </div>
    </div>
  );

  return (
    <div style={S.root}><Fonts/>
      <Nav active={page} onNav={setPage} onBack={page !== "home" ? () => setPage("home") : null} />
      <main style={{ animation: "fadeIn .3s ease-out" }}>
        {page === "home" && <Hero data={data} onNav={setPage} />}
        {page === "career" && <CareerPathMP data={data} />}
        {page === "lineup" && <XIMP data={data} />}
        {page === "whoscored" && <WhoScoredMP data={data} />}
        {page === "tictactoe" && <TicTacToe data={data} />}
      </main>
      <footer style={{ textAlign: "center", padding: "40px 24px", borderTop: "1px solid #252525", color: "#333", fontSize: 11, fontFamily: "'DM Mono', monospace", letterSpacing: 1 }}>
        SÜPER LIG CHALLENGE — DATA FROM SOFASCORE & TRANSFERMARKT
      </footer>
    </div>
  );
}

const S = { root: { minHeight: "100vh", background: "#121212", color: "#e0e0e0", fontFamily: "'Outfit','Inter','Segoe UI',sans-serif" } };

function Fonts() {
  return <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@400;500&family=Outfit:wght@300;400;500;600;700&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    ::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:#121212}::-webkit-scrollbar-thumb{background:#333;border-radius:3px}
    ::selection{background:#C8102E;color:#fff}
    @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
    button{font-family:'Outfit','Inter',sans-serif;cursor:pointer}select option{background:#111;color:#eee}
    input{font-family:'Outfit','Inter',sans-serif}
  `}</style>;
}


// ═══════════════════════════════════════════════════════════════
// NAV
// ═══════════════════════════════════════════════════════════════
function Nav({ active, onNav, onBack }) {
  const tabs = [
    { id: "home", l: "Home" },
    { id: "career", l: "Career Path" },
    { id: "lineup", l: "Starting XI" },
    { id: "whoscored", l: "Who Scored" },
    { id: "tictactoe", l: "Tic-Tac-Toe" },
  ];
  return (
    <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "#121212ee", borderBottom: "1px solid #252525", display: "flex", alignItems: "center", padding: "0 12px", backdropFilter: "blur(12px)" }}>
      {onBack && <button onClick={onBack} style={{ background: "none", border: "none", color: "#C8102E", padding: "14px 8px 14px 0", fontSize: 16 }}>←</button>}
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: "#C8102E", letterSpacing: 3, padding: "10px 12px 10px 4px", borderRight: "1px solid #252525", marginRight: 4 }}>SL</div>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onNav(t.id)} style={{
          background: "none", border: "none",
          borderBottom: active === t.id ? "2px solid #C8102E" : "2px solid transparent",
          color: active === t.id ? "#C8102E" : "#666", padding: "14px 16px 12px",
          fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 2,
          fontFamily: "'Bebas Neue','Oswald',sans-serif", transition: "all .2s",
        }}>{t.l}</button>
      ))}
    </nav>
  );
}


// ═══════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════
function Hero({ data, onNav }) {
  const cc = data.career_challenges || {};
  const totalCareer = Object.values(cc).reduce((s, arr) => s + arr.length, 0);
  const xiCount = (data.xi_challenges || []).length;
  const tttClubs = Object.keys(data.tictactoe?.club_players || {}).length;

  return (
    <div>
      {/* Hero */}
      <div style={{ background: "linear-gradient(180deg, #200010 0%, #121212 100%)", padding: "80px 24px 60px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, background: "radial-gradient(circle, rgba(200,16,46,0.06), transparent 70%)", borderRadius: "50%" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 100, fontWeight: 400, color: "#C8102E", letterSpacing: 12, lineHeight: 1, opacity: 0.08, position: "absolute", top: -20, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap" }}>SÜPER LIG</div>

          {/* Four crests */}
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 32 }}>
            {[
              { name: "GS", colors: ["#FDB913", "#C8102E"] },
              { name: "FB", colors: ["#FFED00", "#00205B"] },
              { name: "BJK", colors: ["#fff", "#000"] },
              { name: "TS", colors: ["#7B2D26", "#1B4B8A"] },
            ].map((c, i) => (
              <div key={i} style={{ width: 56, height: 56, borderRadius: "50%", background: `linear-gradient(135deg, ${c.colors[0]}, ${c.colors[1]})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 30px ${c.colors[0]}15`, border: "2px solid #252525" }}>
                <span style={{ fontSize: 16, fontWeight: 900, color: c.name === "BJK" ? "#000" : "#fff", fontFamily: "'Bebas Neue',sans-serif" }}>{c.name}</span>
              </div>
            ))}
          </div>

          <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 48, color: "#e0e0e0", letterSpacing: 8 }}>SÜPER LIG CHALLENGE</h1>
          <p style={{ fontSize: 14, color: "#666", letterSpacing: 6, textTransform: "uppercase", marginTop: 6, fontFamily: "'DM Mono',monospace" }}>Turkish Football Quiz Hub</p>

          <div style={{ display: "flex", justifyContent: "center", gap: 40, marginTop: 48 }}>
            {[
              { l: "Career Paths", v: totalCareer },
              { l: "XI Challenges", v: xiCount },
              { l: "TTT Clubs", v: tttClubs },
            ].map((s, i) => (
              <div key={i}>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, color: "#C8102E" }}>{s.v}</div>
                <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, textTransform: "uppercase", fontFamily: "'DM Mono',monospace" }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Game Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12, padding: "40px 24px", maxWidth: 960, margin: "0 auto" }}>
        {[
          { id: "career", icon: "🛤️", t: "Career Path", d: `${totalCareer} players — 4 difficulty modes`, c: "#f39c12" },
          { id: "lineup", icon: "⚽", t: "Starting XI", d: `${xiCount} derby lineups to guess`, c: "#2ecc71" },
          { id: "whoscored", icon: "🥅", t: "Who Scored", d: `Guess the goalscorers from derby matches`, c: "#9b59b6" },
          { id: "tictactoe", icon: "#️⃣", t: "Tic-Tac-Toe", d: `${tttClubs} clubs — Easy, Medium, Hard`, c: "#e74c3c" },
        ].map(g => (
          <button key={g.id} onClick={() => onNav(g.id)}
            style={{ background: "#1e1e1e", border: "2px solid #252525", borderRadius: 14, padding: "36px 28px", textAlign: "left", transition: "all .3s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = g.c; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#252525"; e.currentTarget.style.transform = "none"; }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>{g.icon}</div>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 26, color: g.c, letterSpacing: 2 }}>{g.t}</div>
            <p style={{ fontSize: 15, color: "#888", marginTop: 10, lineHeight: 1.6, fontFamily: "'Outfit', sans-serif" }}>{g.d}</p>
          </button>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// TIC-TAC-TOE FOOTBALL
// ═══════════════════════════════════════════════════════════════
function TicTacToe({ data }) {
  const ttt = data.tictactoe || {};
  const clubPlayersData = useMemo(() => ttt.club_players || {}, [ttt]);
  const bigClubs = useMemo(() => new Set(ttt.big_clubs || []), [ttt]);
  const slClubs = useMemo(() => new Set(ttt.sl_clubs || [...bigClubs]), [ttt, bigClubs]);
  const top5Clubs = useMemo(() => new Set(ttt.top5_clubs || []), [ttt]);
  const eliteClubs = useMemo(() => new Set(ttt.elite_clubs || []), [ttt]);
  const currentSLClubs = useMemo(() => new Set(ttt.current_sl_clubs || []), [ttt]);
  const mediumSLClubs = useMemo(() => new Set(ttt.medium_sl_clubs || ttt.current_sl_clubs || []), [ttt]);
  const notablePlayers = useMemo(() => new Set(ttt.notable_players || []), [ttt]);
  const allPlayerNames = useMemo(() => [...new Set(Object.values(clubPlayersData).flat())].sort(), [clubPlayersData]);

  // Club pools for different difficulties
  const goodClubs = useMemo(() =>
    Object.entries(clubPlayersData)
      .map(([club, pls]) => ({ club, count: pls.length }))
      .filter(c => c.count >= 3)
      .sort((a, b) => b.count - a.count),
    [clubPlayersData]
  );

  // Easy pool: SL clubs + Big 4 + elite foreign clubs only
  const easyClubs = useMemo(() =>
    goodClubs.filter(c => bigClubs.has(c.club) || slClubs.has(c.club) || eliteClubs.has(c.club)),
    [goodClubs, bigClubs, slClubs, eliteClubs]
  );

  // Medium pool: SL clubs + Big 4 + all top-5 league clubs
  const easyMediumClubs = useMemo(() =>
    goodClubs.filter(c => bigClubs.has(c.club) || slClubs.has(c.club) || top5Clubs.has(c.club)),
    [goodClubs, bigClubs, slClubs, top5Clubs]
  );

  const [board, setBoard] = useState(null);
  const [grid, setGrid] = useState(Array(9).fill(null));
  const [activeCell, setActiveCell] = useState(null);
  const [turn, setTurn] = useState("X");
  const [mode, setMode] = useState(null);      // "solo" | "vs"
  const [difficulty, setDifficulty] = useState(null); // "easy" | "medium" | "hard"
  const [message, setMessage] = useState("");

  const getShared = useCallback((c1, c2) => {
    const s1 = new Set(clubPlayersData[c1] || []);
    return (clubPlayersData[c2] || []).filter(p => s1.has(p));
  }, [clubPlayersData]);

  const generateBoard = useCallback((diff) => {
    const bigList = goodClubs.filter(c => bigClubs.has(c.club)).map(c => c.club);
    const allList = goodClubs.map(c => c.club);
    
    // SL clubs (non-Big4)
    const slOnlyList = goodClubs.filter(c => slClubs.has(c.club) && !bigClubs.has(c.club)).map(c => c.club);
    // Elite foreign clubs for easy mode
    const eliteList = easyClubs.filter(c => eliteClubs.has(c.club)).map(c => c.club);
    // Medium pool: SL + top-5 European
    const emList = easyMediumClubs.map(c => c.club);
    // Easy pool: SL + elite foreign only
    const easyList = easyClubs.map(c => c.club);
    // Hard pool: all clubs except Big 4
    const hardList = goodClubs.filter(c => !bigClubs.has(c.club)).map(c => c.club);
    
    const isSL = (c) => slClubs.has(c) || bigClubs.has(c);

    if (allList.length < 6) return null;

    const findCompatible = (requiredClubs, excludeClubs, pool) => {
      const exclude = new Set(excludeClubs);
      const candidates = (pool || allList).filter(c => !exclude.has(c));
      return shuffle(candidates.filter(candidate => 
        requiredClubs.every(rc => getShared(rc, candidate).length > 0)
      ));
    };
    
    // Check that every cell has at least one notable player (1M+ fee) as answer
    const hasNotableAnswers = (rows, cols) => {
      for (let ri = 0; ri < rows.length; ri++) {
        for (let ci = 0; ci < cols.length; ci++) {
          const shared = getShared(rows[ri], cols[ci]);
          if (!shared.some(p => notablePlayers.has(p))) return false;
        }
      }
      return true;
    };
    
    const validateNoForeignPairs = (rows, cols) => {
      for (let ri = 0; ri < rows.length; ri++) {
        for (let ci = 0; ci < cols.length; ci++) {
          if (!isSL(rows[ri]) && !isSL(cols[ci])) return false;
        }
      }
      return true;
    };

    for (let attempt = 0; attempt < 500; attempt++) {
      let rows, cols;

      if (diff === "easy") {
        // Rows: 2 Big 4 + 1 current SL club (non-Big4)
        const sb = shuffle([...bigList]);
        if (sb.length < 2) return null;
        const currentSLOnly = goodClubs.filter(c => currentSLClubs.has(c.club) && !bigClubs.has(c.club)).map(c => c.club);
        const thirdRow = shuffle(currentSLOnly.filter(c => c !== sb[0] && c !== sb[1]))[0];
        if (!thirdRow) continue;
        rows = [sb[0], sb[1], thirdRow];
        
        // Cols: from current SL + elite foreign only
        const easyPool = goodClubs.filter(c => currentSLClubs.has(c.club) || bigClubs.has(c.club) || eliteClubs.has(c.club)).map(c => c.club);
        const compatible = findCompatible(rows, rows, easyPool);
        if (compatible.length < 3) continue;
        
        // Try to get at least 1 elite foreign club in columns
        const compatElite = compatible.filter(c => eliteClubs.has(c));
        const compatSL = compatible.filter(c => !eliteClubs.has(c));
        if (compatElite.length >= 1 && compatSL.length >= 2) {
          cols = [compatElite[0], compatSL[0], compatSL[1]];
        } else {
          cols = compatible.slice(0, 3);
        }
        
        if (!validateNoForeignPairs(rows, cols)) continue;
        // For easy: prefer boards where all answers include a notable (500k+) player
        if (notablePlayers.size > 0 && !hasNotableAnswers(rows, cols)) continue;
      } else if (diff === "medium") {
        // 1 Big 4 + elite European clubs (same pool as easy, but only 1 Big 4)
        const sb = shuffle([...bigList]);
        if (sb.length < 1) return null;
        // Other rows: from current SL clubs (non-Big4) or elite foreign
        const otherPool = goodClubs.filter(c => (currentSLClubs.has(c.club) || eliteClubs.has(c.club)) && !bigClubs.has(c.club)).map(c => c.club);
        const others = shuffle(otherPool.filter(c => c !== sb[0]));
        if (others.length < 2) continue;
        rows = [sb[0], others[0], others[1]];
        // Cols: from current SL + elite foreign
        const medPool = goodClubs.filter(c => currentSLClubs.has(c.club) || bigClubs.has(c.club) || eliteClubs.has(c.club)).map(c => c.club);
        const compatible = findCompatible(rows, rows, medPool);
        if (compatible.length < 3) continue;
        cols = compatible.slice(0, 3);
        if (!validateNoForeignPairs(rows, cols)) continue;
      } else {
        // Hard: 0 or 1 Big 4 + SL clubs & top-5 European leagues
        const useBig4 = Math.random() < 0.5 && bigList.length > 0;
        const hardSLOnly = goodClubs.filter(c => mediumSLClubs.has(c.club) && !bigClubs.has(c.club)).map(c => c.club);
        if (useBig4) {
          const sb = shuffle([...bigList]);
          const others = shuffle(hardSLOnly.filter(c => c !== sb[0]));
          if (others.length < 2) continue;
          rows = [sb[0], others[0], others[1]];
        } else {
          const others = shuffle(hardSLOnly);
          if (others.length < 3) continue;
          rows = [others[0], others[1], others[2]];
        }
        const hardPool = goodClubs.filter(c => mediumSLClubs.has(c.club) || bigClubs.has(c.club) || top5Clubs.has(c.club)).map(c => c.club);
        const compatible = findCompatible(rows, rows, hardPool);
        if (compatible.length < 3) continue;
        cols = compatible.slice(0, 3);
        if (!validateNoForeignPairs(rows, cols)) continue;
      }

      return { rows: shuffle(rows), cols: shuffle(cols) };
    }
    return null;
  }, [goodClubs, bigClubs, slClubs, eliteClubs, easyClubs, easyMediumClubs, currentSLClubs, mediumSLClubs, top5Clubs, notablePlayers, getShared]);

  const newGame = useCallback((diff) => {
    const d = diff || difficulty || "easy";
    setBoard(generateBoard(d));
    setGrid(Array(9).fill(null));
    setActiveCell(null);
    setTurn("X");
    setMessage("");
  }, [generateBoard, difficulty]);

  if (!ttt.club_players || Object.keys(ttt.club_players).length === 0)
    return <div style={{ padding: 40, textAlign: "center", color: "#555" }}>No tic-tac-toe data available. Run the scraper first.</div>;

  // Mode selection
  if (!mode) return (
    <div style={{ padding: 24, maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: "#e74c3c", letterSpacing: 4, marginBottom: 10 }}>TIC-TAC-TOE FOOTBALL</h3>
      <div style={{ background: "#1e1e1e", borderRadius: 12, padding: "20px 24px", marginBottom: 24, border: "2px solid #2a2a2a", textAlign: "left" }}>
        <p style={{ fontSize: 15, color: "#bbb", lineHeight: 1.7, fontFamily: "'Outfit', sans-serif" }}>
          Name a player who played for <span style={{ color: "#e74c3c", fontWeight: 600 }}>BOTH</span> the row club and the column club. 
          Each cell needs a different player.
        </p>
        <p style={{ fontSize: 14, color: "#888", lineHeight: 1.6, marginTop: 10, fontFamily: "'Outfit', sans-serif" }}>
          In Solo mode, fill all 9 cells. In VS mode, take turns with a friend and try to get 3 in a row.
          Stuck? Click a cell and use <span style={{ color: "#888", fontWeight: 600 }}>Give Up</span> to reveal an answer, or <span style={{ color: "#888", fontWeight: 600 }}>Reveal All</span> to see every answer.
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button onClick={() => setMode("solo")} style={{ background: "#1e1e1e", border: "2px solid #e74c3c30", borderRadius: 12, padding: "20px 24px", textAlign: "left" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#e74c3c", letterSpacing: 2 }}>SOLO</div>
          <p style={{ fontSize: 14, color: "#888", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>Fill all 9 cells correctly</p>
        </button>
        <button onClick={() => setMode("vs")} style={{ background: "#1e1e1e", border: "2px solid #3498db30", borderRadius: 12, padding: "20px 24px", textAlign: "left" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#3498db", letterSpacing: 2 }}>VS FRIEND</div>
          <p style={{ fontSize: 14, color: "#888", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>Take turns  — get 3 in a row to win</p>
        </button>
      </div>
    </div>
  );

  // Difficulty selection
  if (!difficulty) return (
    <div style={{ padding: 24, maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
      <button onClick={() => setMode(null)} style={{ background: "none", border: "none", color: "#C8102E", fontSize: 14, marginBottom: 16, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, display: "block" }}>← BACK</button>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: "#e74c3c", letterSpacing: 3, marginBottom: 24 }}>CHOOSE DIFFICULTY</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {[
          { id: "easy", label: "🟢 EASY", desc: "2 Big 4 clubs + current SL & elite European clubs", color: "#2ecc71" },
          { id: "medium", label: "🟡 MEDIUM", desc: "1 Big 4 + current SL & elite European clubs", color: "#f39c12" },
          { id: "hard", label: "🔴 HARD", desc: "1 Big 4 + SL clubs & top-5 European leagues", color: "#e74c3c" },
        ].map(d => (
          <button key={d.id} onClick={() => { setDifficulty(d.id); newGame(d.id); }}
            style={{ background: "#1e1e1e", border: `2px solid ${d.color}30`, borderRadius: 12, padding: "18px 24px", textAlign: "left", transition: "all .3s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = d.color} onMouseLeave={e => e.currentTarget.style.borderColor = `${d.color}30`}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: d.color, letterSpacing: 2 }}>{d.label}</div>
            <p style={{ fontSize: 14, color: "#888", marginTop: 6, fontFamily: "'Outfit', sans-serif" }}>{d.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );

  const rows = board?.rows || [], cols = board?.cols || [];
  const isValid = (rc, cc, name) => getShared(rc, cc).some(v => normalize(v) === normalize(name));
  const checkWin = (g) => { for (const [a, b, c] of [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]]) { if (g[a] && g[a].turn === g[b]?.turn && g[a].turn === g[c]?.turn) return g[a].turn; } return null; };

  const handleSelect = (name) => {
    if (activeCell === null) return;
    const ri = Math.floor(activeCell / 3), ci = activeCell % 3;
    if (isValid(rows[ri], cols[ci], name)) {
      const ng = [...grid]; ng[activeCell] = { name, turn, correct: true }; setGrid(ng); setMessage("");
      if (mode === "vs") { const w = checkWin(ng); if (w) setMessage(`${w} WINS!`); else setTurn(turn === "X" ? "O" : "X"); }
    } else {
      setMessage(`${name} didn't play for both ${rows[Math.floor(activeCell / 3)]} and ${cols[activeCell % 3]}`);
      if (mode === "vs") setTurn(turn === "X" ? "O" : "X");
    }
    setActiveCell(null);
  };

  const correctCount = grid.filter(g => g?.correct).length;

  if (!board) return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <div style={{ color: "#e74c3c", fontSize: 14, marginBottom: 12 }}>Couldn't generate a board for this difficulty. Try again.</div>
      <button onClick={() => newGame()} style={{ background: "#C8102E", border: "none", borderRadius: 6, color: "#fff", padding: "10px 20px", fontFamily: "'Bebas Neue', sans-serif" }}>TRY AGAIN</button>
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 600, margin: "0 auto" }}>
      <button onClick={() => { setDifficulty(null); setBoard(null); }} style={{ background: "none", border: "none", color: "#C8102E", fontSize: 13, marginBottom: 12, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1 }}>← CHANGE DIFFICULTY</button>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#e74c3c", letterSpacing: 3 }}>TIC-TAC-TOE FOOTBALL</h3>
        <div style={{ fontSize: 10, color: "#555", fontFamily: "'DM Mono', monospace", marginTop: 4 }}>
          {difficulty === "easy" ? "🟢 EASY" : difficulty === "medium" ? "🟡 MEDIUM" : "🔴 HARD"}
        </div>
        {mode === "vs" && <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: turn === "X" ? "#e74c3c" : "#3498db", letterSpacing: 1, marginTop: 4 }}>TURN: {turn}</div>}
        {mode === "solo" && <div style={{ fontSize: 12, color: "#666", fontFamily: "'DM Mono', monospace", marginTop: 4 }}>{correctCount}/9</div>}
        {message && <div style={{ fontSize: 13, color: message.includes("WINS") ? "#2ecc71" : "#e74c3c", fontFamily: "'Bebas Neue', sans-serif", marginTop: 4, letterSpacing: 1 }}>{message}</div>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "100px 1fr 1fr 1fr", gap: 0, marginBottom: 16 }}>
        <div />
        {cols.map((c, i) => {
          const color = bigClubs.has(c) ? getClubColor(c) : slClubs.has(c) ? "#C8102E" : "#8e8e8e";
          return (
            <div key={i} style={{ padding: "8px 4px", textAlign: "center", fontFamily: "'Bebas Neue', sans-serif", fontSize: 11, color, letterSpacing: 1, borderBottom: "1px solid #333" }}>{c}</div>
          );
        })}
        {rows.map((r, ri) => {
          const rowColor = bigClubs.has(r) ? getClubColor(r) : slClubs.has(r) ? "#C8102E" : "#8e8e8e";
          return (
          <React.Fragment key={ri}>
            <div style={{ padding: "8px 6px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 11, color: rowColor, letterSpacing: 1, display: "flex", alignItems: "center", borderRight: "1px solid #333" }}>{r}</div>
            {cols.map((c, ci) => {
              const idx = ri * 3 + ci; const cell = grid[idx];
              return (
                <button key={ci} onClick={() => !cell && setActiveCell(idx)} style={{
                  background: cell?.revealed ? "#121212" : cell?.correct ? (cell.turn === "X" ? "#1a0a0a" : "#0a0a1a") : activeCell === idx ? "#252525" : "#121212",
                  border: `1px solid ${activeCell === idx ? "#C8102E" : cell?.revealed ? "#33333350" : cell?.correct ? (cell.turn === "X" ? "#e74c3c30" : "#3498db30") : "#252525"}`,
                  padding: "12px 4px", minHeight: 60, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: cell ? "default" : "pointer",
                }}>
                  {cell ? (
                    <div>
                      {cell.revealed ? (
                        <div style={{ fontSize: 9, color: "#666", fontFamily: "'DM Mono', monospace", maxWidth: 80, textAlign: "center", lineHeight: 1.3 }}>
                          {cell.name}
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: 10, fontWeight: 700, color: cell.turn === "X" ? "#e74c3c" : "#3498db", fontFamily: "'Bebas Neue', sans-serif" }}>{cell.turn}</div>
                          <div style={{ fontSize: 9, color: "#888", fontFamily: "'DM Mono', monospace", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cell.name?.split(" ").pop()}</div>
                        </>
                      )}
                    </div>
                  ) : (<div style={{ fontSize: 16, color: "#222" }}>+</div>)}
                </button>
              );
            })}
          </React.Fragment>
          );
        })}
      </div>

      {activeCell !== null && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: "#666", fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>Player who played for {rows[Math.floor(activeCell / 3)]} AND {cols[activeCell % 3]}:</div>
          <PlayerAutocomplete players={allPlayerNames} onSelect={handleSelect} placeholder="Type player name..." autoFocus />
          <button onClick={() => {
            const ri = Math.floor(activeCell / 3), ci = activeCell % 3;
            const answers = shuffle(getShared(rows[ri], cols[ci]));
            if (answers.length > 0) {
              const shown = answers.slice(0, 3);
              const ng = [...grid];
              ng[activeCell] = { name: shown.join(", "), turn: "?", correct: true, revealed: true };
              setGrid(ng);
              setMessage(answers.length > 3 ? `+${answers.length - 3} more possible` : "");
            }
            setActiveCell(null);
          }} style={{ display: "block", width: "100%", marginTop: 6, background: "none", border: "1px dashed #555", borderRadius: 6, color: "#888", padding: "6px", fontSize: 10, fontFamily: "'DM Mono', monospace", textTransform: "uppercase", letterSpacing: 1, cursor: "pointer" }}>
            GIVE UP — REVEAL ANSWER (0 pts)
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
        <button onClick={() => newGame()} style={{ background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#C8102E", padding: "8px 18px", fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500 }}>NEW BOARD</button>
        <button onClick={() => {
          // Reveal all empty cells
          const ng = [...grid];
          for (let i = 0; i < 9; i++) {
            if (!ng[i]) {
              const ri = Math.floor(i / 3), ci = i % 3;
              const answers = shuffle(getShared(rows[ri], cols[ci]));
              if (answers.length > 0) {
                ng[i] = { name: answers.slice(0, 3).join(", "), turn: "?", correct: true, revealed: true };
              }
            }
          }
          setGrid(ng);
          setActiveCell(null);
          setMessage("All answers revealed");
        }} style={{ background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#888", padding: "8px 18px", fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500 }}>REVEAL ALL</button>
        <button onClick={() => { setMode(null); setDifficulty(null); setBoard(null); }} style={{ background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#888", padding: "8px 18px", fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500 }}>CHANGE MODE</button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// CAREER PATH CHALLENGE
// ═══════════════════════════════════════════════════════════════
function CareerPath({ data, mpMode, mpCategory, onMpRoundDone }) {
  const [category, setCategory] = useState(mpCategory || null);
  const allChallenges = data.career_challenges || {};

  const filtered = useMemo(() => {
    if (!category) return [];
    return shuffle([...(allChallenges[category] || [])]);
  }, [category, allChallenges]);

  const [idx, setIdx] = useState(0);
  const [revealOrder, setRevealOrder] = useState(null); // shuffled indices for reveal order
  const [revealCount, setRevealCount] = useState(0); // how many from revealOrder are shown
  const [guess, setGuess] = useState("");
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [nameHintUsed, setNameHintUsed] = useState(false);
  const [hasUserRevealed, setHasUserRevealed] = useState(false);

  const cur = filtered[idx];

  // Initialize reveal order when player changes
  useEffect(() => {
    if (!cur) return;
    const path = cur.career_path || [];
    const indices = [...Array(path.length).keys()];
    const shuffled = shuffle(indices);
    const halfCount = Math.ceil(path.length / 2);
    setRevealOrder(shuffled);
    setRevealCount(halfCount);
    setNameHintUsed(false);
    setHasUserRevealed(false);
    setResult(null);
    setGuess("");
  }, [idx, cur?.answer]);

  const allPlayerNames = useMemo(() => {
    const names = new Set();
    Object.values(allChallenges).forEach(arr => arr.forEach(c => names.add(c.answer)));
    return [...names].sort();
  }, [allChallenges]);

  const categoryCounts = useMemo(() => {
    const c = {};
    for (const k of ["big_easy","big_recent","big_all","gs_recent","gs_all","fb_recent","fb_all","bjk_recent","bjk_all","ts_recent","ts_all","legends","legends_200","all_recent","all_all"]) {
      c[k] = (allChallenges[k] || []).length;
    }
    return c;
  }, [allChallenges]);

  // Category selection
  if (!category) return (
    <div style={{ padding: 24, maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: "#f39c12", letterSpacing: 4, marginBottom: 10 }}>CAREER PATH</h3>
      <div style={{ background: "#1e1e1e", borderRadius: 12, padding: "20px 24px", marginBottom: 24, border: "2px solid #2a2a2a", textAlign: "left" }}>
        <p style={{ fontSize: 15, color: "#bbb", lineHeight: 1.7, fontFamily: "'Outfit', sans-serif" }}>
          A player's career history is revealed one club at a time. The fewer clubs you need to see, the more points you earn.
        </p>
        <p style={{ fontSize: 14, color: "#888", lineHeight: 1.6, marginTop: 10, fontFamily: "'Outfit', sans-serif" }}>
          Use <span style={{ color: "#C8102E", fontWeight: 600 }}>Reveal Next Club</span> to see more clubs (fewer points), 
          or <span style={{ color: "#3498db", fontWeight: 600 }}>Name Hint</span> to see the letter count (-1 point).
          You can also <span style={{ color: "#888", fontWeight: 600 }}>Give Up</span> to see the answer (0 points).
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* General modes */}
        <div style={{ fontSize: 11, color: "#555", fontFamily: "'Outfit', sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: 2, marginTop: 8, textAlign: "left" }}>General</div>
        {[
          { id: "big_easy", label: "BIG CLUBS — EASY", desc: "GS, FB, BJK players with international careers", color: "#1abc9c", icon: "⭐" },
          { id: "big_recent", label: "BIG 4 — RECENT", desc: "GS, FB, BJK, TS since 2015", color: "#2ecc71", icon: "🟢" },
          { id: "big_all", label: "BIG 4 — ALL", desc: "GS, FB, BJK, TS since 2000", color: "#f39c12", icon: "🟡" },
          { id: "legends", label: "SÜPER LIG LEGENDS", desc: "Players with 100+ Süper Lig appearances", color: "#e67e22", icon: "🏆" },
          { id: "legends_200", label: "LEGENDS 200+", desc: "Players with 200+ Süper Lig appearances", color: "#c0392b", icon: "👑" },
        ].map(e => (
          <button key={e.id}
            onClick={() => { setCategory(e.id); setIdx(0); setRevealed(1); setResult(null); setScore(0); setTotal(0); }}
            disabled={categoryCounts[e.id] === 0}
            style={{ background: "#1e1e1e", border: `2px solid ${e.color}30`, borderRadius: 12, padding: "16px 24px", textAlign: "left", transition: "all .3s", opacity: categoryCounts[e.id] === 0 ? 0.4 : 1 }}
            onMouseEnter={ev => ev.currentTarget.style.borderColor = e.color}
            onMouseLeave={ev => ev.currentTarget.style.borderColor = `${e.color}30`}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: e.color, letterSpacing: 2 }}>{e.icon} {e.label}</div>
            <p style={{ fontSize: 13, color: "#888", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>{e.desc} — {categoryCounts[e.id]} players</p>
          </button>
        ))}

        {/* Individual clubs */}
        <div style={{ fontSize: 11, color: "#555", fontFamily: "'Outfit', sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: 2, marginTop: 12, textAlign: "left" }}>By Club</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[
            { id: "gs_recent", label: "GS Recent", color: "#FDB913" },
            { id: "gs_all", label: "GS All", color: "#FDB913" },
            { id: "fb_recent", label: "FB Recent", color: "#FFED00" },
            { id: "fb_all", label: "FB All", color: "#FFED00" },
            { id: "bjk_recent", label: "BJK Recent", color: "#ccc" },
            { id: "bjk_all", label: "BJK All", color: "#ccc" },
            { id: "ts_recent", label: "TS Recent", color: "#7B2D26" },
            { id: "ts_all", label: "TS All", color: "#7B2D26" },
          ].map(e => (
            <button key={e.id}
              onClick={() => { setCategory(e.id); setIdx(0); setRevealed(1); setResult(null); setScore(0); setTotal(0); }}
              disabled={categoryCounts[e.id] === 0}
              style={{ background: "#1e1e1e", border: `2px solid ${e.color}25`, borderRadius: 10, padding: "12px 16px", textAlign: "left", transition: "all .3s", opacity: categoryCounts[e.id] === 0 ? 0.4 : 1 }}
              onMouseEnter={ev => ev.currentTarget.style.borderColor = e.color}
              onMouseLeave={ev => ev.currentTarget.style.borderColor = `${e.color}25`}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: e.color, letterSpacing: 1 }}>{e.label}</div>
              <p style={{ fontSize: 12, color: "#666", marginTop: 2, fontFamily: "'Outfit', sans-serif" }}>{categoryCounts[e.id]} players</p>
            </button>
          ))}
        </div>

        {/* All clubs modes */}
        <div style={{ fontSize: 11, color: "#555", fontFamily: "'Outfit', sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: 2, marginTop: 12, textAlign: "left" }}>All Clubs</div>
        {[
          { id: "all_recent", label: "ALL CLUBS — RECENT", desc: "Any Süper Lig club since 2015", color: "#3498db", icon: "🔵" },
          { id: "all_all", label: "ALL CLUBS — ALL", desc: "Any Süper Lig club since 2000", color: "#e74c3c", icon: "🔴" },
        ].map(e => (
          <button key={e.id}
            onClick={() => { setCategory(e.id); setIdx(0); setRevealed(1); setResult(null); setScore(0); setTotal(0); }}
            disabled={categoryCounts[e.id] === 0}
            style={{ background: "#1e1e1e", border: `2px solid ${e.color}30`, borderRadius: 12, padding: "18px 24px", textAlign: "left", transition: "all .3s", opacity: categoryCounts[e.id] === 0 ? 0.4 : 1 }}
            onMouseEnter={ev => ev.currentTarget.style.borderColor = e.color}
            onMouseLeave={ev => ev.currentTarget.style.borderColor = `${e.color}30`}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: e.color, letterSpacing: 2 }}>{e.icon} {e.label}</div>
            <p style={{ fontSize: 14, color: "#888", marginTop: 6, fontFamily: "'Outfit', sans-serif" }}>{e.desc}  — {categoryCounts[e.id]} players</p>
          </button>
        ))}
      </div>
    </div>
  );

  if (!cur) return <div style={{ padding: 40, textAlign: "center", color: "#555" }}>No challenges for this category.</div>;

  const path = cur.career_path || [];
  const revealedIndices = new Set(revealOrder ? revealOrder.slice(0, revealCount) : []);
  const totalClubs = path.length;
  const unrevealed = totalClubs - revealedIndices.size;
  const maxPts = Math.max(1, totalClubs - 1);
  const pts = Math.max(1, unrevealed + 1);

  // Generate name hint: "---- -----" for "Ryan Babel"
  const nameHint = cur.answer.split(" ").map(part => part.split("").map(ch => ch === "-" ? " " : "—").join("")).join("  ");

  const check = (name) => {
    const g = name || guess;
    const an = normalize(cur.answer), gn = normalize(g);
    const ok = gn === an || an.includes(gn) || gn.includes(an.split(" ").pop());
    setResult({ ok, answer: cur.answer });
    setTotal(t => t + 1);
    const finalPts = nameHintUsed ? Math.max(1, pts - 1) : pts;
    if (ok) setScore(s => s + finalPts);
  };

  const next = () => { setIdx(i => (i + 1) % filtered.length); };
  const revealNext = () => { if (revealCount < totalClubs) { setRevealCount(c => c + 1); setHasUserRevealed(true); } };

  // Determine which club to highlight (SL clubs this player played for)
  const slClubs = new Set(cur.sl_clubs || []);

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      {mpMode && <MultiplayerBanner mp={mpMode} />}
      <button onClick={() => setCategory(null)} style={{ background: "none", border: "none", color: "#C8102E", fontSize: 13, marginBottom: 12, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1 }}>← CHANGE CATEGORY</button>
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#f39c12", letterSpacing: 3, marginBottom: 2 }}>CAREER PATH</h3>
        <div style={{ fontSize: 10, color: "#666", fontFamily: "'DM Mono', monospace" }}>
          {{"big_easy":"⭐ Big Clubs — Easy","big_recent":"🟢 Big 4 — Since 2015","big_all":"🟡 Big 4 — Since 2000","gs_recent":"GS — Since 2015","gs_all":"GS — All","fb_recent":"FB — Since 2015","fb_all":"FB — All","bjk_recent":"BJK — Since 2015","bjk_all":"BJK — All","ts_recent":"TS — Since 2015","ts_all":"TS — All","legends":"🏆 Süper Lig Legends 100+","legends_200":"👑 Legends 200+","all_recent":"🔵 All Clubs — Since 2015","all_all":"🔴 All Clubs — Since 2000"}[category] || category}
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 6 }}>
          <span style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#C8102E", fontSize: 16 }}>SCORE: {score}</span>
          <span style={{ fontFamily: "'DM Mono', monospace", color: "#555", fontSize: 11, alignSelf: "center" }}>{total > 0 ? `${total} played` : ""}</span>
        </div>
      </div>

      {/* Side-by-side layout: table on left, input/buttons on right */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>

      {/* Career path - compact table */}
      <div style={{ flex: "1 1 420px", background: "#1a1a1a", borderRadius: 12, padding: "14px 18px", border: "2px solid #252525" }}>
        <div style={{ textAlign: "center", marginBottom: 10 }}>
          <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500, color: "#888" }}>
            {!result ? `${nameHintUsed ? Math.max(1, pts - 1) : pts} point${(nameHintUsed ? Math.max(1, pts - 1) : pts) > 1 ? "s" : ""} if you guess now${nameHintUsed ? " (hint used)" : ""}` : ""}
          </span>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'DM Mono', monospace" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #2a2a2a" }}>
              <th style={{ textAlign: "left", padding: "4px 8px", fontSize: 11, color: "#888", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Season</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontSize: 11, color: "#888", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Club</th>
            </tr>
          </thead>
          <tbody>
            {path.map((club, i) => {
              const isRevealed = result || revealedIndices.has(i);
              const isLastRevealed = hasUserRevealed && !result && revealOrder && revealOrder[revealCount - 1] === i;
              const isLoan = isRevealed && club.type === "loan";
              const textColor = isLastRevealed ? "#f59e0b" : isRevealed ? "#e0e0e0" : "#333";
              const seasonColor = isLastRevealed ? "#f59e0b" : isRevealed ? "#888" : "#333";
              return (
                <tr key={i} style={{ borderBottom: i < path.length - 1 ? "1px solid #1f1f1f" : "none", background: isLastRevealed ? "#1f1708" : "transparent" }}>
                  <td style={{ padding: "6px 8px", fontSize: 13, color: seasonColor, whiteSpace: "nowrap" }}>
                    {isRevealed ? (club.season || "-") : "--"}
                  </td>
                  <td style={{ padding: "6px 8px", fontSize: 14, color: textColor, fontFamily: "'Outfit', sans-serif", fontWeight: isLastRevealed ? 700 : 500 }}>
                    {isRevealed ? (
                      <>
                        {club.club}
                        {isLoan && <span style={{ color: "#f39c12", fontSize: 12, marginLeft: 6, fontStyle: "italic" }}>(loan)</span>}
                      </>
                    ) : "???"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Input area - wrapped in a flex item to sit beside the table */}
      <div style={{ flex: "1 1 380px", minWidth: 280 }}>
      {!result ? (
        <div style={{ background: "#252525", borderRadius: 10, padding: 20, border: "2px solid #222" }}>
          {/* Name hint */}
          {nameHintUsed && (
            <div style={{ textAlign: "center", marginBottom: 14, padding: "14px 18px", background: "#0a0a1a", border: "2px solid #3498db40", borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: "#3498db", fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", letterSpacing: 2, marginBottom: 6, fontWeight: 600 }}>NAME HINT</div>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, color: "#3498db", letterSpacing: 5 }}>{nameHint}</div>
              <div style={{ fontSize: 11, color: "#555", fontFamily: "'DM Mono', monospace", marginTop: 6 }}>{cur.answer.split(" ").map(p => p.length).join(" + ")} letters</div>
            </div>
          )}
          <div style={{ fontSize: 13, color: "#888", fontFamily: "'Outfit', sans-serif", marginBottom: 8, fontWeight: 500 }}>Who is this player?</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <PlayerAutocomplete players={allPlayerNames} onSelect={check} placeholder="Type player name..." autoFocus />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={revealNext} disabled={revealCount >= totalClubs}
              style={{ flex: 1, background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#C8102E", padding: "10px", fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, opacity: revealCount >= totalClubs ? 0.3 : 1, cursor: revealCount >= totalClubs ? "default" : "pointer" }}>
              REVEAL NEXT CLUB ({unrevealed > 1 ? `${unrevealed} hidden` : "all revealed"})
            </button>
            {!nameHintUsed && (
              <button onClick={() => setNameHintUsed(true)}
                style={{ background: "#0a0a1a", border: "2px solid #3498db40", borderRadius: 8, color: "#3498db", padding: "10px 16px", fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, cursor: "pointer", whiteSpace: "nowrap" }}>
                🔤 HINT (-1pt)
              </button>
            )}
          </div>
          <button onClick={() => { setResult({ ok: false, answer: cur.answer }); setTotal(t => t + 1); }}
            style={{ display: "block", width: "100%", marginTop: 10, background: "none", border: "2px dashed #333", borderRadius: 8, color: "#555", padding: "8px", fontSize: 12, fontFamily: "'Outfit', sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: 1, cursor: "pointer" }}>
            GIVE UP — REVEAL ANSWER (0 pts)
          </button>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 24, borderRadius: 12, background: result.ok ? "#0a2e0a" : "#2e0a0a", border: `2px solid ${result.ok ? "#2ecc7150" : "#e74c3c50"}` }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: result.ok ? "#2ecc71" : "#e74c3c", letterSpacing: 2 }}>{result.ok ? `CORRECT! +${pts} pts` : "WRONG!"}</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#C8102E", marginTop: 6, letterSpacing: 1 }}>{result.answer}</div>
          {mpMode ? (
            <button onClick={() => onMpRoundDone(score)} style={{ marginTop: 16, background: "#C8102E", border: "none", borderRadius: 8, color: "#fff", padding: "10px 28px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: 2 }}>END TURN — {score} pts</button>
          ) : (
            <button onClick={next} style={{ marginTop: 16, background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#C8102E", padding: "10px 28px", fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 600 }}>NEXT PLAYER →</button>
          )}
        </div>
      )}
      </div>

      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// STARTING XI CHALLENGE
// ═══════════════════════════════════════════════════════════════
function XIChallenge({ data, mpMode, onMpRoundDone }) {
  const chs = data.xi_challenges || [];
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [filterTeam, setFilterTeam] = useState("all");
  const [filterSeason, setFilterSeason] = useState("all");

  const allPlayerNames = useMemo(() => {
    const names = new Set();
    chs.forEach(c => {
      c.home_starting_xi?.forEach(n => names.add(n));
      c.away_starting_xi?.forEach(n => names.add(n));
    });
    return [...names].sort();
  }, [chs]);

  const seasons = useMemo(() => [...new Set(chs.map(c => c.season).filter(Boolean))].sort().reverse(), [chs]);
  const teams = useMemo(() => {
    const t = new Set();
    chs.forEach(c => { t.add(c.home_team); t.add(c.away_team); });
    return [...t].sort();
  }, [chs]);

  const filtered = useMemo(() => {
    return chs.filter(c => {
      if (filterTeam !== "all" && c.home_team !== filterTeam && c.away_team !== filterTeam) return false;
      if (filterSeason !== "all" && c.season !== filterSeason) return false;
      return true;
    });
  }, [chs, filterTeam, filterSeason]);

  if (!chs.length) return <div style={{ padding: 40, textAlign: "center", color: "#555" }}>No lineup challenges available. Run the derby scraper first!</div>;

  // Match selection
  if (!selectedMatch) return (
    <div style={{ padding: 24, maxWidth: 700, margin: "0 auto" }}>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: "#2ecc71", letterSpacing: 4, textAlign: "center", marginBottom: 8 }}>STARTING XI</h3>
      <div style={{ background: "#1e1e1e", borderRadius: 12, padding: "18px 22px", marginBottom: 20, border: "2px solid #2a2a2a", textAlign: "left" }}>
        <p style={{ fontSize: 15, color: "#bbb", lineHeight: 1.7, fontFamily: "'Outfit', sans-serif" }}>
          Both teams' lineups are shown side by side. <span style={{ color: "#2ecc71", fontWeight: 600 }}>3 players per team</span> are hidden. 
          Click a hidden player and guess their name.
        </p>
        <p style={{ fontSize: 14, color: "#888", lineHeight: 1.6, marginTop: 8, fontFamily: "'Outfit', sans-serif" }}>
          You get 3 attempts per player: <span style={{ color: "#2ecc71" }}>3pts</span> first try, <span style={{ color: "#f39c12" }}>2pts</span> second, <span style={{ color: "#e74c3c" }}>1pt</span> third. Max score: 18 points.
        </p>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)}
          style={{ background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#C8102E", padding: "6px 12px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, letterSpacing: 2 }}>
          <option value="all">All Teams</option>
          {teams.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterSeason} onChange={e => setFilterSeason(e.target.value)}
          style={{ background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#C8102E", padding: "6px 12px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, letterSpacing: 2 }}>
          <option value="all">All Seasons</option>
          {seasons.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => { if (filtered.length) setSelectedMatch(filtered[Math.floor(Math.random() * filtered.length)]); }}
          style={{ marginLeft: "auto", background: "#2ecc71", border: "none", borderRadius: 6, color: "#121212", padding: "6px 16px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 13, letterSpacing: 1 }}>
          RANDOM
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {filtered.map((ch, i) => {
          const homeColor = getClubColor(ch.home_team);
          const awayColor = getClubColor(ch.away_team);
          return (
            <button key={i} onClick={() => setSelectedMatch(ch)} style={{
              background: "#252525", border: "none", borderLeft: `3px solid ${homeColor}`,
              borderRadius: "0 6px 6px 0", padding: "10px 16px", textAlign: "left",
              display: "grid", gridTemplateColumns: "85px 1fr 50px", alignItems: "center", gap: 12,
              transition: "background .2s",
            }}
              onMouseEnter={e => e.currentTarget.style.background = "#161616"}
              onMouseLeave={e => e.currentTarget.style.background = "#111"}>
              <div>
                <div style={{ fontSize: 11, color: "#888", fontFamily: "'DM Mono', monospace" }}>{ch.date}</div>
                <div style={{ fontSize: 8, color: "#444", fontFamily: "'DM Mono', monospace", textTransform: "uppercase", marginTop: 2 }}>{ch.season}</div>
              </div>
              <div>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 14, color: homeColor, letterSpacing: 1 }}>{ch.home_team}</span>
                <span style={{ color: "#555", fontSize: 12, margin: "0 8px" }}>vs</span>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 14, color: awayColor, letterSpacing: 1 }}>{ch.away_team}</span>
              </div>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: "#2ecc71", textAlign: "center", letterSpacing: 2 }}>{ch.score}</div>
            </button>
          );
        })}
      </div>
      {filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "#555" }}>No matches for this filter.</div>}
    </div>
  );

  return <XIGame ch={selectedMatch} allPlayerNames={allPlayerNames} onBack={() => setSelectedMatch(null)} mpMode={mpMode} onMpRoundDone={onMpRoundDone} />;
}

function XIGame({ ch, allPlayerNames, onBack, mpMode, onMpRoundDone }) {
  // Hidden indices and guesses for BOTH teams
  const [homeHidden, setHomeHidden] = useState([]);
  const [awayHidden, setAwayHidden] = useState([]);
  const [homeGuesses, setHomeGuesses] = useState({});  // idx -> {attempts: [{guess, correct}], done: bool, answer: str}
  const [awayGuesses, setAwayGuesses] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [activeSlot, setActiveSlot] = useState(null); // {side: "home"|"away", idx: number}
  const [score, setScore] = useState(0);

  const homePlayers = ch.home_lineup || ch.home_starting_xi.map((name, i) => ({
    name, shortName: name.split(" ").pop(), position: i === 0 ? "G" : i <= 4 ? "D" : i <= 7 ? "M" : "F",
    number: "", goals: "0", minutes: "90", substitute: false,
  }));

  const awayPlayers = ch.away_lineup || ch.away_starting_xi.map((name, i) => ({
    name, shortName: name.split(" ").pop(), position: i === 0 ? "G" : i <= 4 ? "D" : i <= 7 ? "M" : "F",
    number: "", goals: "0", minutes: "90", substitute: false,
  }));

  const pickHidden = (players, formation) => {
    const starters = players.filter(p => !p.substitute);
    const formationNums = (formation || "").split("-").map(Number).filter(n => n > 0);
    let indices = [];
    if (formationNums.length > 0 && formationNums.reduce((a, b) => a + b, 0) === starters.length - 1) {
      const rows = [];
      let idx = 1;
      for (const count of formationNums) { const row = []; for (let i = 0; i < count; i++) { row.push(idx); idx++; } rows.push(row); }
      const shuffledRows = shuffle([...rows]);
      for (const row of shuffledRows.slice(0, 3)) indices.push(row[Math.floor(Math.random() * row.length)]);
    } else {
      const groups = { G: [], D: [], M: [], F: [] };
      starters.forEach((p, i) => { const pos = (p.position || "M").toUpperCase(); if (groups[pos]) groups[pos].push(i); else groups.M.push(i); });
      const nonEmpty = Object.values(groups).filter(g => g.length > 0);
      for (const g of shuffle([...nonEmpty]).slice(0, 3)) indices.push(g[Math.floor(Math.random() * g.length)]);
    }
    if (indices.length < 3) { indices = shuffle([...Array(Math.min(starters.length, 11)).keys()]).slice(0, 3); }
    return indices;
  };

  useEffect(() => {
    setHomeHidden(pickHidden(homePlayers, ch.home_formation));
    setAwayHidden(pickHidden(awayPlayers, ch.away_formation));
    setHomeGuesses({}); setAwayGuesses({}); setRevealed(false); setActiveSlot(null); setScore(0);
  }, [ch]);

  const handleGuess = (name) => {
    if (!name || !activeSlot) return;
    const { side, idx } = activeSlot;
    const players = side === "home" ? homePlayers : awayPlayers;
    const xiList = side === "home" ? ch.home_starting_xi : ch.away_starting_xi;
    const correct = players[idx]?.name || xiList[idx];
    const gn = normalize(name), cn = normalize(correct);
    const isCorrect = gn === cn || cn.includes(gn) || gn.includes(cn.split(" ").pop());
    
    const guesses = side === "home" ? homeGuesses : awayGuesses;
    const setGuesses = side === "home" ? setHomeGuesses : setAwayGuesses;
    const prev = guesses[idx] || { attempts: [], done: false, answer: correct };
    const newAttempts = [...prev.attempts, { guess: name, correct: isCorrect }];
    const attemptsUsed = newAttempts.length;
    const maxAttempts = 3;
    
    // Points: 3 for 1st attempt, 2 for 2nd, 1 for 3rd, 0 if all wrong
    if (isCorrect) {
      const pts = Math.max(1, maxAttempts - attemptsUsed + 1); // 3, 2, or 1
      setScore(s => s + pts);
      setGuesses(p => ({ ...p, [idx]: { attempts: newAttempts, done: true, correct: true, answer: correct, points: pts } }));
      setActiveSlot(null);
    } else if (attemptsUsed >= maxAttempts) {
      // All 3 attempts used, reveal the answer
      setGuesses(p => ({ ...p, [idx]: { attempts: newAttempts, done: true, correct: false, answer: correct, points: 0 } }));
      setActiveSlot(null);
    } else {
      // Wrong but still has attempts left
      setGuesses(p => ({ ...p, [idx]: { ...prev, attempts: newAttempts } }));
      // Keep activeSlot open for next attempt
    }
  };

  const homeColor = getClubColor(ch.home_team);
  const awayColor = getClubColor(ch.away_team);
  const totalHidden = homeHidden.length + awayHidden.length;
  const maxScore = totalHidden * 3; // 6 players × 3 pts each = 18

  // Convert guesses to PitchLineup format
  const toPitchGuesses = (guesses) => {
    const out = {};
    for (const [idx, g] of Object.entries(guesses)) {
      if (g.done) {
        out[idx] = { guess: g.attempts[g.attempts.length - 1].guess, correct: g.correct, answer: g.answer };
      }
    }
    return out;
  };

  // Check if a slot is still guessable (not done yet)
  const isSlotActive = (side, idx) => {
    const guesses = side === "home" ? homeGuesses : awayGuesses;
    return !guesses[idx]?.done;
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      {mpMode && <MultiplayerBanner mp={mpMode} />}
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#C8102E", fontSize: 13, marginBottom: 12, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1 }}>← PICK ANOTHER DERBY</button>

      <div style={{ background: "#252525", borderRadius: 10, padding: "20px", textAlign: "center", marginBottom: 16, border: "2px solid #252525" }}>
        <div style={{ fontSize: 10, color: "#555", fontFamily: "'DM Mono', monospace", textTransform: "uppercase", letterSpacing: 2 }}>Süper Lig · {ch.date}</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, margin: "8px 0", letterSpacing: 2 }}>
          <span style={{ color: homeColor }}>{ch.home_team}</span>
          <span style={{ color: "#555", margin: "0 12px" }}>{ch.score}</span>
          <span style={{ color: awayColor }}>{ch.away_team}</span>
        </div>
        <div style={{ fontSize: 12, color: "#2ecc71", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1 }}>
          SCORE: {score}/{maxScore}
        </div>
        <div style={{ fontSize: 9, color: "#555", fontFamily: "'DM Mono', monospace", marginTop: 4 }}>3 chances per player · 3pts → 2pts → 1pt</div>
      </div>

      {/* Two pitches side by side */}
      <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
        <PitchLineup
          players={homePlayers} teamName={ch.home_team} color={homeColor}
          hiddenIndices={revealed ? null : homeHidden} guesses={toPitchGuesses(homeGuesses)}
          onPlayerClick={(idx) => isSlotActive("home", idx) && setActiveSlot({ side: "home", idx })}
          formation={ch.home_formation}
        />
        <PitchLineup
          players={awayPlayers} teamName={ch.away_team} color={awayColor} isAway={true}
          hiddenIndices={revealed ? null : awayHidden} guesses={toPitchGuesses(awayGuesses)}
          onPlayerClick={(idx) => isSlotActive("away", idx) && setActiveSlot({ side: "away", idx })}
          formation={ch.away_formation}
        />
      </div>

      {/* Autocomplete input — below pitches, sticky at bottom */}
      {activeSlot && isSlotActive(activeSlot.side, activeSlot.idx) && (
        <div style={{ position: "sticky", bottom: 0, background: "#121212", borderTop: "2px solid #252525", padding: "12px 0", marginTop: 12, zIndex: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: "#888", fontFamily: "'Outfit', sans-serif" }}>
              Guess the hidden <span style={{ color: activeSlot.side === "home" ? homeColor : awayColor, fontWeight: 600 }}>{activeSlot.side === "home" ? ch.home_team : ch.away_team}</span> player:
            </span>
            {(() => {
              const g = (activeSlot.side === "home" ? homeGuesses : awayGuesses)[activeSlot.idx];
              const used = g?.attempts?.length || 0;
              return <span style={{ fontSize: 12, color: used === 0 ? "#2ecc71" : used === 1 ? "#f39c12" : "#e74c3c", fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>
                {3 - used}pt{3 - used !== 1 ? "s" : ""} · attempt {used + 1}/3
              </span>;
            })()}
          </div>
          {(() => {
            const g = (activeSlot.side === "home" ? homeGuesses : awayGuesses)[activeSlot.idx];
            if (!g?.attempts?.length) return null;
            return <div style={{ marginBottom: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {g.attempts.filter(a => !a.correct).map((a, i) => (
                <span key={i} style={{ fontSize: 11, color: "#e74c3c", fontFamily: "'DM Mono', monospace", background: "#1a0a0a", padding: "2px 8px", borderRadius: 4, border: "1px solid #e74c3c30" }}>
                  ✗ {a.guess}
                </span>
              ))}
            </div>;
          })()}
          <PlayerAutocomplete players={allPlayerNames} onSelect={handleGuess} placeholder="Player name..." autoFocus openUp />
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "center", flexWrap: "wrap" }}>
        {!revealed && <button onClick={() => setRevealed(true)} style={{ background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#888", padding: "8px 18px", fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500 }}>REVEAL ALL</button>}
        {mpMode && <button onClick={() => onMpRoundDone(score)} style={{ background: "#C8102E", border: "none", borderRadius: 8, color: "#fff", padding: "8px 24px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: 2 }}>END TURN — {score} pts</button>}
      </div>
    </div>
  );
}
function WhoScored({ data, mpMode, onMpRoundDone }) {
  const chs = data.xi_challenges || [];
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [filterTeam, setFilterTeam] = useState("all");
  const [filterSeason, setFilterSeason] = useState("all");

  // Only show matches that have goal data
  const matchesWithGoals = useMemo(() => chs.filter(c => c.goals && c.goals.length > 0), [chs]);

  const seasons = useMemo(() => [...new Set(matchesWithGoals.map(c => c.season).filter(Boolean))].sort().reverse(), [matchesWithGoals]);
  const teams = useMemo(() => {
    const t = new Set();
    matchesWithGoals.forEach(c => { t.add(c.home_team); t.add(c.away_team); });
    return [...t].sort();
  }, [matchesWithGoals]);

  const filtered = useMemo(() => {
    return matchesWithGoals.filter(c => {
      if (filterTeam !== "all" && c.home_team !== filterTeam && c.away_team !== filterTeam) return false;
      if (filterSeason !== "all" && c.season !== filterSeason) return false;
      return true;
    });
  }, [matchesWithGoals, filterTeam, filterSeason]);

  // Build full player list from all club data for autocomplete
  const allDBPlayers = useMemo(() => {
    const names = new Set();
    const cp = data.tictactoe?.club_players || {};
    Object.values(cp).forEach(arr => arr.forEach(n => names.add(n)));
    return [...names].sort();
  }, [data]);

  if (!matchesWithGoals.length) return <div style={{ padding: 40, textAlign: "center", color: "#555" }}>No goal data available. Re-run the derby scraper to fetch goal events.</div>;

  if (selectedMatch) return <WhoScoredGame ch={selectedMatch} allDBPlayers={allDBPlayers} onBack={() => setSelectedMatch(null)} mpMode={mpMode} onMpRoundDone={onMpRoundDone} />;

  return (
    <div style={{ padding: 24, maxWidth: 700, margin: "0 auto" }}>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: "#9b59b6", letterSpacing: 4, textAlign: "center", marginBottom: 8 }}>WHO SCORED</h3>
      <div style={{ background: "#1e1e1e", borderRadius: 12, padding: "18px 22px", marginBottom: 20, border: "2px solid #2a2a2a", textAlign: "left" }}>
        <p style={{ fontSize: 15, color: "#bbb", lineHeight: 1.7, fontFamily: "'Outfit', sans-serif" }}>
          For each derby match, the goal minutes are shown with dashes representing the scorer's name.
          Click a goal and guess who scored it.
        </p>
        <p style={{ fontSize: 14, color: "#888", lineHeight: 1.6, marginTop: 8, fontFamily: "'Outfit', sans-serif" }}>
          Each dash represents one letter. Spaces between dashes separate first and last names.
        </p>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)}
          style={{ background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#9b59b6", padding: "6px 12px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, letterSpacing: 2 }}>
          <option value="all">All Teams</option>
          {teams.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterSeason} onChange={e => setFilterSeason(e.target.value)}
          style={{ background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#9b59b6", padding: "6px 12px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, letterSpacing: 2 }}>
          <option value="all">All Seasons</option>
          {seasons.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => { if (filtered.length) setSelectedMatch(filtered[Math.floor(Math.random() * filtered.length)]); }}
          style={{ marginLeft: "auto", background: "#9b59b6", border: "none", borderRadius: 6, color: "#fff", padding: "6px 16px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 13, letterSpacing: 1 }}>
          RANDOM
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {filtered.map((ch, i) => {
          const homeColor = getClubColor(ch.home_team);
          return (
            <button key={i} onClick={() => setSelectedMatch(ch)} style={{
              background: "#252525", border: "none", borderLeft: "3px solid #9b59b6",
              borderRadius: "0 6px 6px 0", padding: "10px 16px", textAlign: "left",
              display: "grid", gridTemplateColumns: "85px 1fr 50px", alignItems: "center", gap: 12,
              transition: "background .2s",
            }}
              onMouseEnter={e => e.currentTarget.style.background = "#161616"}
              onMouseLeave={e => e.currentTarget.style.background = "#111"}>
              <div>
                <div style={{ fontSize: 11, color: "#888", fontFamily: "'DM Mono', monospace" }}>{ch.date}</div>
                <div style={{ fontSize: 8, color: "#444", fontFamily: "'DM Mono', monospace", textTransform: "uppercase", marginTop: 2 }}>{ch.season}</div>
              </div>
              <div>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 14, color: homeColor, letterSpacing: 1 }}>{ch.home_team}</span>
                <span style={{ color: "#555", fontSize: 12, margin: "0 8px" }}>vs</span>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 14, color: getClubColor(ch.away_team), letterSpacing: 1 }}>{ch.away_team}</span>
              </div>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: "#9b59b6", textAlign: "center", letterSpacing: 2 }}>{ch.score}</div>
            </button>
          );
        })}
      </div>
      {filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "#555" }}>No matches for this filter.</div>}
    </div>
  );
}

function WhoScoredGame({ ch, allDBPlayers, onBack, mpMode, onMpRoundDone }) {
  const goals = ch.goals || [];
  const [guesses, setGuesses] = useState({});
  const [activeGoal, setActiveGoal] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);

  // Use all players from the database for autocomplete (not just match players)
  const allPlayerNames = useMemo(() => {
    const names = new Set(allDBPlayers || []);
    // Also ensure goal scorers are included
    (ch.goals || []).forEach(g => { if (g.player) names.add(g.player); });
    return [...names].sort();
  }, [ch, allDBPlayers]);

  // Separate goals by side
  const homeGoals = goals.filter(g => g.side === "home");
  const awayGoals = goals.filter(g => g.side === "away");

  const homeColor = getClubColor(ch.home_team);
  const awayColor = getClubColor(ch.away_team);

  const handleGuess = (name) => {
    if (!name || activeGoal === null) return;
    const goal = goals[activeGoal];
    const correct = goal.player;
    const gn = normalize(name), cn = normalize(correct);
    const gLast = gn.split(" ").pop(), cLast = cn.split(" ").pop();
    const isCorrect = gn === cn || cn.includes(gn) || gn.includes(cn) || gLast === cLast || gn.includes(cLast) || cn.includes(gLast);
    setGuesses(prev => ({ ...prev, [activeGoal]: { guess: name, correct: isCorrect, answer: correct } }));
    if (isCorrect) setScore(s => s + 1);
    setActiveGoal(null);
  };

  const makeHint = (name) => {
    return name.split(" ").map(part => part.split("").map(ch => ch === "-" ? " " : "—").join("")).join("  ");
  };

  const totalGoals = goals.length;
  const correctCount = Object.values(guesses).filter(g => g.correct).length;

  const renderGoalRow = (goal, globalIdx) => {
    const g = guesses[globalIdx];
    const isRevealed = revealed || g;
    const isCorrect = g?.correct;
    const goalType = goal.type === "penalty" ? " (P)" : goal.type === "ownGoal" ? " (OG)" : "";

    return (
      <div key={globalIdx} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        {/* Name area */}
        <div style={{ flex: 1 }}>
          {isRevealed ? (
            <span style={{
              fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: 1,
              color: isCorrect ? "#2ecc71" : g ? "#e74c3c" : "#9b59b6",
            }}>
              {isCorrect ? g.guess : (g ? g.answer : goal.player)}{goalType}
              {g && !isCorrect && <span style={{ fontSize: 11, color: "#666", marginLeft: 8 }}>(you said: {g.guess})</span>}
            </span>
          ) : (
            <button onClick={() => setActiveGoal(globalIdx)} style={{
              background: activeGoal === globalIdx ? "#1a1a2a" : "transparent", border: "none",
              cursor: "pointer", padding: "4px 0",
            }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: "#9b59b6", letterSpacing: 3 }}>
                {makeHint(goal.player)}{goalType}
              </span>
            </button>
          )}
        </div>
        {/* Minute */}
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#888", whiteSpace: "nowrap" }}>
          {goal.minute}'
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 700, margin: "0 auto" }}>
      {mpMode && <MultiplayerBanner mp={mpMode} />}
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#9b59b6", fontSize: 13, marginBottom: 12, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1 }}>← PICK ANOTHER DERBY</button>

      {/* Match header */}
      <div style={{ background: "#252525", borderRadius: 12, padding: "24px", textAlign: "center", marginBottom: 20, border: "2px solid #252525" }}>
        <div style={{ fontSize: 10, color: "#555", fontFamily: "'DM Mono', monospace", textTransform: "uppercase", letterSpacing: 2, marginBottom: 8 }}>Süper Lig · {ch.date}</div>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 20 }}>
          <div style={{ flex: 1, textAlign: "right" }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: homeColor, letterSpacing: 2 }}>{ch.home_team}</div>
          </div>
          <div style={{
            fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: "#fff", letterSpacing: 4,
            background: "#9b59b6", borderRadius: 10, padding: "6px 20px", minWidth: 80, textAlign: "center",
          }}>{ch.score}</div>
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: awayColor, letterSpacing: 2 }}>{ch.away_team}</div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#9b59b6", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, marginTop: 12 }}>
          GUESS THE SCORERS ({correctCount}/{totalGoals})
        </div>
      </div>

      {/* Autocomplete input */}
      {activeGoal !== null && !guesses[activeGoal] && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: "#666", fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>
            Who scored the {goals[activeGoal].minute}' goal for {goals[activeGoal].side === "home" ? ch.home_team : ch.away_team}?
          </div>
          <PlayerAutocomplete players={allPlayerNames} onSelect={handleGuess} placeholder="Player name..." autoFocus />
        </div>
      )}

      {/* Goals display - two columns */}
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        {/* Home goals */}
        <div style={{ flex: 1, minWidth: 250 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 14, color: homeColor, letterSpacing: 2, marginBottom: 10, borderBottom: `1px solid ${homeColor}30`, paddingBottom: 6 }}>{ch.home_team}</div>
          {homeGoals.length === 0 && <div style={{ color: "#333", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>No goals</div>}
          {homeGoals.map(g => {
            const globalIdx = goals.indexOf(g);
            return renderGoalRow(g, globalIdx);
          })}
        </div>
        {/* Away goals */}
        <div style={{ flex: 1, minWidth: 250 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 14, color: awayColor, letterSpacing: 2, marginBottom: 10, borderBottom: `1px solid ${awayColor}30`, paddingBottom: 6 }}>{ch.away_team}</div>
          {awayGoals.length === 0 && <div style={{ color: "#333", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>No goals</div>}
          {awayGoals.map(g => {
            const globalIdx = goals.indexOf(g);
            return renderGoalRow(g, globalIdx);
          })}
        </div>
      </div>

      {/* Buttons */}
      <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "center", flexWrap: "wrap" }}>
        {!revealed && <button onClick={() => setRevealed(true)} style={{ background: "#252525", border: "2px solid #333", borderRadius: 8, color: "#888", padding: "8px 18px", fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500 }}>REVEAL ALL</button>}
        {mpMode && <button onClick={() => onMpRoundDone(score)} style={{ background: "#C8102E", border: "none", borderRadius: 8, color: "#fff", padding: "8px 24px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: 2 }}>END TURN — {score} pts</button>}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// MULTIPLAYER WRAPPERS
// ═══════════════════════════════════════════════════════════════

function CareerPathMP({ data }) {
  const [mode, setMode] = useState(null); // null=choose, "solo", "mp_setup", "mp_category", "mp_play", "mp_waiting", "mp_done"
  const [mp, setMp] = useState(null);
  const [mpCategory, setMpCategory] = useState(null);

  if (!mode) return (
    <div style={{ padding: 24, maxWidth: 420, margin: "0 auto", textAlign: "center" }}>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: "#f39c12", letterSpacing: 4, marginBottom: 20 }}>CAREER PATH</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button onClick={() => setMode("solo")} style={{ background: "#1e1e1e", border: "2px solid #f39c1230", borderRadius: 12, padding: "20px 24px", textAlign: "left" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#f39c12", letterSpacing: 2 }}>SOLO</div>
          <p style={{ fontSize: 14, color: "#888", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>Play by yourself</p>
        </button>
        <button onClick={() => setMode("mp_setup")} style={{ background: "#1e1e1e", border: "2px solid #C8102E30", borderRadius: 12, padding: "20px 24px", textAlign: "left" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#C8102E", letterSpacing: 2 }}>MULTIPLAYER</div>
          <p style={{ fontSize: 14, color: "#888", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>2-4 players take turns</p>
        </button>
      </div>
    </div>
  );

  if (mode === "solo") return <CareerPath data={data} />;

  if (mode === "mp_setup") return <MultiplayerSetup gameType="career" onCancel={() => setMode(null)} onStart={(cfg) => { setMp({...cfg}); setMode("mp_category"); }} />;

  // Category selection for multiplayer — choose once, applies to all turns
  if (mode === "mp_category") {
    const allChallenges = data.career_challenges || {};
    const cats = [
      { id: "big_easy", label: "BIG CLUBS — EASY", color: "#1abc9c" },
      { id: "big_recent", label: "BIG 4 — RECENT", color: "#2ecc71" },
      { id: "big_all", label: "BIG 4 — ALL", color: "#f39c12" },
      { id: "legends", label: "LEGENDS 100+", color: "#e67e22" },
      { id: "legends_200", label: "LEGENDS 200+", color: "#c0392b" },
      { id: "gs_recent", label: "GS RECENT", color: "#FDB913" },
      { id: "fb_recent", label: "FB RECENT", color: "#FFED00" },
      { id: "bjk_recent", label: "BJK RECENT", color: "#ccc" },
      { id: "ts_recent", label: "TS RECENT", color: "#7B2D26" },
      { id: "all_recent", label: "ALL CLUBS — RECENT", color: "#3498db" },
      { id: "all_all", label: "ALL CLUBS — ALL", color: "#e74c3c" },
    ];
    return (
      <div style={{ padding: 24, maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
        <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: "#f39c12", letterSpacing: 3, marginBottom: 16 }}>CHOOSE CATEGORY</h3>
        <p style={{ fontSize: 13, color: "#888", fontFamily: "'Outfit', sans-serif", marginBottom: 16 }}>All players will use this category</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {cats.filter(c => (allChallenges[c.id] || []).length > 0).map(c => (
            <button key={c.id} onClick={() => { setMpCategory(c.id); setMode("mp_waiting"); }}
              style={{ background: "#1e1e1e", border: "2px solid " + c.color + "30", borderRadius: 10, padding: "14px 20px", textAlign: "left", transition: "all .3s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = c.color}
              onMouseLeave={e => e.currentTarget.style.borderColor = c.color + "30"}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: c.color, letterSpacing: 2 }}>{c.label}</div>
              <span style={{ fontSize: 12, color: "#666", fontFamily: "'Outfit', sans-serif" }}>{(allChallenges[c.id] || []).length} players</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (mode === "mp_done") return <MultiplayerResult mp={mp} onPlayAgain={() => { setMp(null); setMpCategory(null); setMode(null); }} />;

  if (mode === "mp_waiting") {
    const playerColors = ["#e74c3c", "#3498db", "#2ecc71", "#f39c12"];
    return (
      <div style={{ padding: 40, maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
        <MultiplayerBanner mp={mp} />
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: playerColors[mp.currentPlayer], letterSpacing: 2, marginBottom: 8 }}>
          {mp.names[mp.currentPlayer]}
        </div>
        <p style={{ fontSize: 15, color: "#888", fontFamily: "'Outfit', sans-serif", marginBottom: 20 }}>
          Round {mp.roundNum + 1} of {mp.rounds}. You will guess 1 player.
        </p>
        <button onClick={() => setMode("mp_play")} style={{ background: "#C8102E", border: "none", borderRadius: 10, color: "#fff", padding: "14px 40px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 2 }}>
          START TURN
        </button>
      </div>
    );
  }

  if (mode === "mp_play") return <CareerPath data={data} mpMode={mp} mpCategory={mpCategory} onMpRoundDone={(roundScore) => {
    const newScores = [...mp.scores];
    newScores[mp.currentPlayer] += roundScore;
    const nextPlayer = mp.currentPlayer + 1;
    if (nextPlayer >= mp.players) {
      const nextRound = mp.roundNum + 1;
      if (nextRound >= mp.rounds) {
        setMp({ ...mp, scores: newScores }); setMode("mp_done");
      } else {
        setMp({ ...mp, scores: newScores, currentPlayer: 0, roundNum: nextRound }); setMode("mp_waiting");
      }
    } else {
      setMp({ ...mp, scores: newScores, currentPlayer: nextPlayer }); setMode("mp_waiting");
    }
  }} />;

  return null;
}

function XIMP({ data }) {
  const [mode, setMode] = useState(null);
  const [mp, setMp] = useState(null);

  if (!mode) return (
    <div style={{ padding: 24, maxWidth: 420, margin: "0 auto", textAlign: "center" }}>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: "#2ecc71", letterSpacing: 4, marginBottom: 20 }}>STARTING XI</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button onClick={() => setMode("solo")} style={{ background: "#1e1e1e", border: "2px solid #2ecc7130", borderRadius: 12, padding: "20px 24px", textAlign: "left" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#2ecc71", letterSpacing: 2 }}>SOLO</div>
          <p style={{ fontSize: 14, color: "#888", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>Play by yourself</p>
        </button>
        <button onClick={() => setMode("mp_setup")} style={{ background: "#1e1e1e", border: "2px solid #C8102E30", borderRadius: 12, padding: "20px 24px", textAlign: "left" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#C8102E", letterSpacing: 2 }}>MULTIPLAYER</div>
          <p style={{ fontSize: 14, color: "#888", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>2-4 players take turns</p>
        </button>
      </div>
    </div>
  );

  if (mode === "solo") return <XIChallenge data={data} />;
  if (mode === "mp_setup") return <MultiplayerSetup gameType="xi" onCancel={() => setMode(null)} onStart={(cfg) => { setMp(cfg); setMode("mp_waiting"); }} />;
  if (mode === "mp_done") return <MultiplayerResult mp={mp} onPlayAgain={() => { setMp(null); setMode(null); }} />;

  if (mode === "mp_waiting") {
    const playerColors = ["#e74c3c", "#3498db", "#2ecc71", "#f39c12"];
    return (
      <div style={{ padding: 40, maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
        <MultiplayerBanner mp={mp} />
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: playerColors[mp.currentPlayer], letterSpacing: 2, marginBottom: 8 }}>
          {mp.names[mp.currentPlayer]}
        </div>
        <p style={{ fontSize: 15, color: "#888", fontFamily: "'Outfit', sans-serif", marginBottom: 20 }}>
          Pick a derby and guess the hidden players. Max 18 points.
        </p>
        <button onClick={() => setMode("mp_play")} style={{ background: "#C8102E", border: "none", borderRadius: 10, color: "#fff", padding: "14px 40px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 2 }}>
          START TURN
        </button>
      </div>
    );
  }

  if (mode === "mp_play") return <XIChallenge data={data} mpMode={mp} onMpRoundDone={(roundScore) => {
    const newScores = [...mp.scores];
    newScores[mp.currentPlayer] += roundScore;
    const nextPlayer = mp.currentPlayer + 1;
    if (nextPlayer >= mp.players) {
      const nextRound = mp.roundNum + 1;
      if (nextRound >= mp.rounds) {
        setMp({ ...mp, scores: newScores }); setMode("mp_done");
      } else {
        setMp({ ...mp, scores: newScores, currentPlayer: 0, roundNum: nextRound }); setMode("mp_waiting");
      }
    } else {
      setMp({ ...mp, scores: newScores, currentPlayer: nextPlayer }); setMode("mp_waiting");
    }
  }} />;

  return null;
}

function WhoScoredMP({ data }) {
  const [mode, setMode] = useState(null);
  const [mp, setMp] = useState(null);

  if (!mode) return (
    <div style={{ padding: 24, maxWidth: 420, margin: "0 auto", textAlign: "center" }}>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: "#9b59b6", letterSpacing: 4, marginBottom: 20 }}>WHO SCORED</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button onClick={() => setMode("solo")} style={{ background: "#1e1e1e", border: "2px solid #9b59b630", borderRadius: 12, padding: "20px 24px", textAlign: "left" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#9b59b6", letterSpacing: 2 }}>SOLO</div>
          <p style={{ fontSize: 14, color: "#888", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>Play by yourself</p>
        </button>
        <button onClick={() => setMode("mp_setup")} style={{ background: "#1e1e1e", border: "2px solid #C8102E30", borderRadius: 12, padding: "20px 24px", textAlign: "left" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#C8102E", letterSpacing: 2 }}>MULTIPLAYER</div>
          <p style={{ fontSize: 14, color: "#888", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>2-4 players take turns</p>
        </button>
      </div>
    </div>
  );

  if (mode === "solo") return <WhoScored data={data} />;
  if (mode === "mp_setup") return <MultiplayerSetup gameType="whoscored" onCancel={() => setMode(null)} onStart={(cfg) => { setMp(cfg); setMode("mp_waiting"); }} />;
  if (mode === "mp_done") return <MultiplayerResult mp={mp} onPlayAgain={() => { setMp(null); setMode(null); }} />;

  if (mode === "mp_waiting") {
    const playerColors = ["#e74c3c", "#3498db", "#2ecc71", "#f39c12"];
    return (
      <div style={{ padding: 40, maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
        <MultiplayerBanner mp={mp} />
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: playerColors[mp.currentPlayer], letterSpacing: 2, marginBottom: 8 }}>
          {mp.names[mp.currentPlayer]}
        </div>
        <p style={{ fontSize: 15, color: "#888", fontFamily: "'Outfit', sans-serif", marginBottom: 20 }}>
          Pick a derby and guess who scored. More correct = more points.
        </p>
        <button onClick={() => setMode("mp_play")} style={{ background: "#C8102E", border: "none", borderRadius: 10, color: "#fff", padding: "14px 40px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 2 }}>
          START TURN
        </button>
      </div>
    );
  }

  if (mode === "mp_play") return <WhoScored data={data} mpMode={mp} onMpRoundDone={(roundScore) => {
    const newScores = [...mp.scores];
    newScores[mp.currentPlayer] += roundScore;
    const nextPlayer = mp.currentPlayer + 1;
    if (nextPlayer >= mp.players) {
      const nextRound = mp.roundNum + 1;
      if (nextRound >= mp.rounds) {
        setMp({ ...mp, scores: newScores }); setMode("mp_done");
      } else {
        setMp({ ...mp, scores: newScores, currentPlayer: 0, roundNum: nextRound }); setMode("mp_waiting");
      }
    } else {
      setMp({ ...mp, scores: newScores, currentPlayer: nextPlayer }); setMode("mp_waiting");
    }
  }} />;

  return null;
}

// ═══════════════════════════════════════════════════════════════
// MULTIPLAYER SETUP & SCOREBOARD
// ═══════════════════════════════════════════════════════════════
function MultiplayerSetup({ onStart, onCancel, gameType }) {
  const [numPlayers, setNumPlayers] = useState(2);
  const [roundLength, setRoundLength] = useState("short");
  const [names, setNames] = useState(["Player 1", "Player 2", "Player 3", "Player 4"]);

  const rounds = gameType === "career" 
    ? (roundLength === "short" ? 3 : 10) 
    : (roundLength === "short" ? 1 : 3);

  return (
    <div style={{ padding: 24, maxWidth: 420, margin: "0 auto", textAlign: "center" }}>
      <button onClick={onCancel} style={{ background: "none", border: "none", color: "#C8102E", fontSize: 14, marginBottom: 16, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, display: "block" }}>← BACK</button>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: "#C8102E", letterSpacing: 3, marginBottom: 16 }}>MULTIPLAYER</h3>
      
      <div style={{ background: "#1e1e1e", borderRadius: 12, padding: "20px 24px", border: "2px solid #2a2a2a", textAlign: "left", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#888", fontFamily: "'Outfit', sans-serif", marginBottom: 8 }}>Number of Players</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[2, 3, 4].map(n => (
            <button key={n} onClick={() => setNumPlayers(n)}
              style={{ flex: 1, background: numPlayers === n ? "#C8102E" : "#252525", border: "2px solid " + (numPlayers === n ? "#C8102E" : "#333"), borderRadius: 8, color: "#fff", padding: "10px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 1 }}>
              {n}
            </button>
          ))}
        </div>

        <div style={{ fontSize: 13, color: "#888", fontFamily: "'Outfit', sans-serif", marginBottom: 8 }}>Game Length</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {["short", "long"].map(l => (
            <button key={l} onClick={() => setRoundLength(l)}
              style={{ flex: 1, background: roundLength === l ? "#C8102E" : "#252525", border: "2px solid " + (roundLength === l ? "#C8102E" : "#333"), borderRadius: 8, color: "#fff", padding: "10px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: 1, textTransform: "uppercase" }}>
              {l} ({gameType === "career" ? (l === "short" ? "3" : "10") : (l === "short" ? "1" : "3")} {gameType === "career" ? "players" : (gameType === "whoscored" ? "derbies" : "derbies")})
            </button>
          ))}
        </div>

        <div style={{ fontSize: 13, color: "#888", fontFamily: "'Outfit', sans-serif", marginBottom: 8 }}>Player Names</div>
        {Array.from({ length: numPlayers }, (_, i) => (
          <input key={i} value={names[i]} onChange={e => { const n = [...names]; n[i] = e.target.value; setNames(n); }}
            style={{ width: "100%", background: "#0e0e0e", border: "2px solid #333", borderRadius: 8, color: "#ddd", padding: "10px 14px", fontSize: 14, marginBottom: 6, fontFamily: "'Outfit', sans-serif", boxSizing: "border-box" }}
          />
        ))}
      </div>

      <button onClick={() => onStart({ players: numPlayers, rounds, names: names.slice(0, numPlayers), currentPlayer: 0, scores: Array(numPlayers).fill(0), roundNum: 0 })}
        style={{ background: "#C8102E", border: "none", borderRadius: 10, color: "#fff", padding: "14px 40px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 2, cursor: "pointer" }}>
        START GAME
      </button>
    </div>
  );
}

function MultiplayerBanner({ mp }) {
  if (!mp) return null;
  const playerColors = ["#e74c3c", "#3498db", "#2ecc71", "#f39c12"];
  return (
    <div style={{ background: "#1a1a1a", borderRadius: 10, padding: "12px 20px", marginBottom: 16, border: "2px solid " + playerColors[mp.currentPlayer] + "40", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
      <div>
        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: playerColors[mp.currentPlayer], letterSpacing: 1 }}>
          {mp.names[mp.currentPlayer]}&apos;s TURN
        </span>
        <span style={{ fontSize: 12, color: "#666", fontFamily: "'DM Mono', monospace", marginLeft: 10 }}>
          Round {mp.roundNum + 1}/{mp.rounds}
        </span>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        {mp.names.map((n, i) => (
          <div key={i} style={{ textAlign: "center", opacity: i === mp.currentPlayer ? 1 : 0.5 }}>
            <div style={{ fontSize: 11, color: playerColors[i], fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{n}</div>
            <div style={{ fontSize: 16, color: "#fff", fontFamily: "'Bebas Neue', sans-serif" }}>{mp.scores[i]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MultiplayerResult({ mp, onPlayAgain }) {
  if (!mp) return null;
  const playerColors = ["#e74c3c", "#3498db", "#2ecc71", "#f39c12"];
  const maxScore = Math.max(...mp.scores);
  const winners = mp.names.filter((_, i) => mp.scores[i] === maxScore);
  
  return (
    <div style={{ padding: 40, maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: "#C8102E", letterSpacing: 4, marginBottom: 8 }}>GAME OVER</h3>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: "#2ecc71", letterSpacing: 2, marginBottom: 20 }}>
        {winners.length === 1 ? ("🏆 " + winners[0] + " WINS!") : ("🤝 TIE: " + winners.join(" & "))}
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 24, marginBottom: 24 }}>
        {mp.names.map((n, i) => (
          <div key={i} style={{ background: "#1e1e1e", borderRadius: 12, padding: "16px 24px", border: "2px solid " + (mp.scores[i] === maxScore ? "#2ecc71" : "#333") }}>
            <div style={{ fontSize: 14, color: playerColors[i], fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{n}</div>
            <div style={{ fontSize: 32, color: "#fff", fontFamily: "'Bebas Neue', sans-serif" }}>{mp.scores[i]}</div>
            <div style={{ fontSize: 11, color: "#666", fontFamily: "'DM Mono', monospace" }}>points</div>
          </div>
        ))}
      </div>
      <button onClick={onPlayAgain} style={{ background: "#C8102E", border: "none", borderRadius: 10, color: "#fff", padding: "12px 32px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, letterSpacing: 2, cursor: "pointer" }}>
        PLAY AGAIN
      </button>
    </div>
  );
}
